import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import { CURSOR_ART } from './cursorArt.mjs';

/**
 * The stage every tool guide plays on: a small page, 240 × 112, where the tool's own cursor
 * does the tool's one job, over and over.
 *
 * The rules that keep fifty of them reading as one set:
 * - One loop, LOOP seconds, for every guide. Times are fractions of it, 0 to 1.
 * - What is already on the page is `.g-obj` (quiet); what the tool makes or changes is
 *   `.g-new` (the accent). A chosen thing has `.g-sel` with `Handle`s.
 * - The cursor is the real cursor art (`Cursor`), moving along the gesture — so the guide
 *   and the canvas agree about what the tool looks like in the hand.
 * - Show the result, then hold it: the last quarter of the loop is still, so the eye can
 *   read what happened before it starts again.
 * - No words inside the picture. The card says the name and one line.
 */
export const LOOP = 3.2;
export const W = 240;
export const H = 112;

/** A frozen clock, for the lab's frame-by-frame review; null plays normally. */
export const GuideClock = createContext<number | null>(null);

export function Stage({ children, label }: { children: ReactNode; label: string }) {
  const ref = useRef<SVGSVGElement>(null);
  const frozen = useContext(GuideClock);
  useEffect(() => {
    const svg = ref.current;
    if (!svg) return;
    const still = frozen ?? (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? LOOP * 0.85 : null);
    if (still === null) return;
    svg.pauseAnimations();
    svg.setCurrentTime(still);
  }, [frozen]);
  return (
    <svg ref={ref} className="tool-guide-picture" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label}>
      {children}
    </svg>
  );
}

/** SMIL timing for one animated attribute: values at each key time (fractions of the loop). */
export function keys(values: readonly (number | string)[], times: readonly number[]) {
  return {
    values: values.join(';'),
    keyTimes: times.join(';'),
    dur: `${LOOP}s`,
    repeatCount: 'indefinite',
    calcMode: 'linear',
  } as const;
}

/** Animates one attribute of its parent. */
export function Anim({ attr, values, times }: { attr: string; values: readonly (number | string)[]; times: readonly number[] }) {
  return <animate attributeName={attr} {...keys(values, times)} />;
}

/** Shows its parent only between two times. */
export function Between({ from, to }: { from: number; to: number }) {
  const a = Math.max(0.0001, from);
  const b = Math.min(0.9999, to);
  return <animate attributeName="opacity" values="0;0;1;1;0;0" keyTimes={`0;${a};${a};${b};${b};1`} dur={`${LOOP}s`} repeatCount="indefinite" calcMode="discrete" />;
}

/**
 * The tool's cursor, carried along `path` (in stage coordinates) with its hotspot on the
 * path: at `along[i]` of the path's length at `times[i]`.
 */
export function Cursor({ k, path, along, times, scale = 0.9 }: { k: string; path: string; along: readonly number[]; times: readonly number[]; scale?: number }) {
  const art = CURSOR_ART[k] ?? CURSOR_ART[k.split(':')[0]!] ?? CURSOR_ART.select!;
  const href = `data:image/svg+xml;utf8,${encodeURIComponent(art.svg)}`;
  return (
    <g>
      <animateMotion path={path} keyPoints={along.join(';')} keyTimes={times.join(';')} dur={`${LOOP}s`} repeatCount="indefinite" calcMode="linear" />
      <image href={href} width={32} height={32} transform={`scale(${scale}) translate(${-art.hotspot[0]} ${-art.hotspot[1]})`} />
    </g>
  );
}

/** A selection handle. */
export function Handle({ x, y }: { x: number; y: number }) {
  return <rect className="g-handle" x={x - 2.5} y={y - 2.5} width={5} height={5} />;
}

/** The four corner handles of a box. */
export function Handles({ x, y, w, h }: { x: number; y: number; w: number; h: number }) {
  return <>{[[x, y], [x + w, y], [x, y + h], [x + w, y + h]].map(([hx, hy]) => <Handle key={`${hx},${hy}`} x={hx!} y={hy!} />)}</>;
}

/** An anchor point on a path. */
export function Anchor({ x, y, chosen = false }: { x: number; y: number; chosen?: boolean }) {
  return <rect className={chosen ? 'g-anchor g-anchor-on' : 'g-anchor'} x={x - 2.2} y={y - 2.2} width={4.4} height={4.4} />;
}

/**
 * A cursor walk as stops: `[x, y, t]` — be at (x, y) at time t. Two stops at the same
 * place hold the cursor still between them. Answers what `Cursor` takes.
 */
export function walk(stops: readonly (readonly [number, number, number])[]) {
  const lengths = [0];
  for (let i = 1; i < stops.length; i++) {
    const [ax, ay] = stops[i - 1]!;
    const [bx, by] = stops[i]!;
    lengths.push(lengths[i - 1]! + Math.hypot(bx - ax, by - ay));
  }
  const total = lengths.at(-1)! || 1;
  const times = stops.map(([, , t]) => t);
  // A walk must span the whole loop; hold the ends.
  const along = lengths.map((l) => Number((l / total).toFixed(4)));
  if (times[0] !== 0) { times.unshift(0); along.unshift(along[0]!); }
  if (times.at(-1) !== 1) { times.push(1); along.push(along.at(-1)!); }
  const path = stops.map(([x, y], i) => `${i ? 'L' : 'M'}${x} ${y}`).join('') + (total === 1 && stops.length === 1 ? 'l0.01 0' : '');
  return { path, along, times };
}

/** The cursor following a walk. */
export function Walk({ k, stops, scale }: { k: string; stops: readonly (readonly [number, number, number])[]; scale?: number }) {
  const { path, along, times } = walk(stops);
  return <Cursor k={k} path={path} along={along} times={times} scale={scale} />;
}

/** A closed outline through points, as a path (for shapes whose outline is animated). */
export function outline(points: readonly (readonly [number, number])[]): string {
  return points.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`).join('') + 'Z';
}
